<template>
  <div class="compair-layout--box">
    <div class="compair-layout--box-header">
      <h3>Filter</h3>
      <div class="vx-row">
        <div class="vx-col w-full">
          <DataInput
            v-if="mode == 'CAMPAIGNS'"
            :value="null"
            :reset="resetSearch"
            type="model"
            name="facebook"
            :gql-query="query"
            :reduce="el => el"
            label="Facebook Kampagnen"
            multiple
            @input="setIds"
          />
          <DataInput
            v-if="mode == 'ADS'"
            :value="null"
            :reset="resetSearch"
            type="model"
            name="facebook"
            :gql-query="query"
            :reduce="el => el"
            label="Facebook Anzeigen"
            multiple
            @input="setIds"
          />
          <DataInput
            v-if="mode == 'ADGROUPS'"
            :value="null"
            :reset="resetSearch"
            type="model"
            name="facebook"
            :gql-query="query"
            :reduce="el => el"
            label="Facebook Anzeigegruppen"
            multiple
            @input="setIds"
          />
        </div>
        <div class="vx-col w-full lg:w-1/2 mt-2">
          <DataInput
            v-model="filter.startDate"
            type="date"
            label="Von"
            name="from"
            :config="{
              altFormat: 'd.m.Y H:i',
              dateFormat: 'Y-m-d H:i'
            }"
          />
        </div>
        <div class="vx-col w-full lg:w-1/2 mt-2">
          <DataInput
            v-model="filter.endDate"
            type="date"
            label="Bis"
            name="to"
            :config="{
              altFormat: 'd.m.Y H:i',
              dateFormat: 'Y-m-d H:i'
            }"
          />
        </div>
      </div>
    </div>
    <div class="compair-layout--box-body">
      <template v-if="filter.ids && datacollection">
        <FacebookStatChart
          ref="chart"
          :chart-data="datacollection"
          :height="250"
          :max="chartMaxY"
          :options="options"
        ></FacebookStatChart>
      </template>
      <template v-else> Bitte Facebook Kampagne auswählen. </template>
    </div>
  </div>
</template>

<script>
import gql from "graphql-tag";
import moment from "moment";
import updateChartYLegendMixin from "@/mixins/updateChartYLegendMixin";
import DataInput from "@/components/ui/DataInput";
import FacebookStatChart from "./charts/FacebookStatChart";
import { fillMissingDates } from "../../helpers/fillMissingDates";

export default {
  components: {
    DataInput,
    FacebookStatChart
  },
  mixins: [updateChartYLegendMixin],
  props: {
    maxY: { type: Number, default: null },
    mode: { type: String, required: true }
  },
  data() {
    return {
      internalMaxY: null,
      datacollection: null,
      filter: {
        ids: null,
        startDate: moment().subtract(7, "days"),
        endDate: null
      },
      resetSearch: false
    };
  },
  apollo: {
    facebookAdOverview: {
      query: gql`
        query FacebookAdOverview(
          $size: Int!
          $filter: FacebookAdInsightFilter
          $datapointStart: Time!
          $datapointEnd: Time
        ) {
          facebookAdOverview(size: $size, filter: $filter) {
            results {
              id
              datapoints(from: $datapointStart, to: $datapointEnd) {
                date
                clicks
                spend
                cpC
                impressions
                ctr
              }
            }
          }
        }
      `,
      variables() {
        return {
          filter: {
            iDs: this.filter.ids
          },
          datapointStart: this.filter.startDate,
          datapointEnd: this.filter.endDate,
          size: 1000
        };
      },
      skip() {
        return !this.filter.ids;
      }
    }
  },
  computed: {
    chartMaxY() {
      return Math.max(this.internalMaxY, this.maxY);
    },
    query() {
      return gql`
        query FacebookAdSearch($search: String){
          facebookAdSearch(page:1, size:15, search:$search, type:${this.queryType}){
            total
            results{
              name,
              id,
              adInsightIDs,
              type
            }
          }
        }
      `;
    },
    queryType() {
      return {
        ADGROUPS: "AD_SET",
        ADS: "AD",
        CAMPAIGNS: "CAMPAIGN"
      }[this.mode];
    }
  },
  watch: {
    facebookAdOverview(value) {
      let stats = {
        datapoints: []
      };
      for (let ad of value.results) {
        stats.datapoints = stats.datapoints.concat(ad.datapoints);
      }
      if (!stats.datapoints) {
        this.datacollection = null;
        return;
      }

      stats.datapoints.sort((a, b) =>
        a.date > b.date ? 1 : a.date < b.date ? -1 : 0
      );

      let labels = [];
      for (let data of stats.datapoints) {
        let date = moment(data.date);
        if (labels.findIndex(val => val.diff(date) === 0) === -1) {
          labels.push(date);
        }
      }

      labels = fillMissingDates(labels);

      let maxValue = 1;
      let clicksData = [];
      let spendData = [];
      let cpCData = [];
      let ctrData = [];
      let impressionsData = [];
      for (let label of labels) {
        let clicks = 0;
        let spend = 0;
        let cpC = 0;
        let ctr = 0;
        let impressions = 0;
        for (let data of stats.datapoints) {
          let time = moment(data.date);
          if (time.diff(label) === 0) {
            clicks = data.clicks;
            spend = data.spend;
            cpC = data.cpC;
            ctr = data.ctr;
            impressions = data.impressions;
          }
        }

        const currentMaxValue = Math.max(clicks, spend, cpC, impressions);
        if (currentMaxValue > maxValue) {
          maxValue = currentMaxValue;
        }

        clicksData.push(clicks);
        spendData.push(spend);
        cpCData.push(cpC);
        ctrData.push(ctr);
        impressionsData.push(impressions);
      }

      this.internalMaxY = maxValue;
      this.$emit("max-y-updated", maxValue);
      this.datacollection = {
        labels,
        datasets: [
          {
            label: "Klicks",
            borderColor: "#13358e",
            data: clicksData,
            fill: true,
            backgroundColor: "rgba(19, 53, 142, 0.8)",
            pointHoverBackgroundColor: "#000000",
            lineTension: 0.4
          },
          {
            label: "Ausgegeben",
            borderColor: "#55c400",
            data: spendData,
            fill: true,
            backgroundColor: "rgba(85, 196, 0, 0.8)",
            pointHoverBackgroundColor: "#000000",
            lineTension: 0.4
          },
          {
            label: "CPC",
            borderColor: "#d1232b",
            data: cpCData,
            fill: true,
            backgroundColor: "rgba(209, 35, 43, 0.8)",
            pointHoverBackgroundColor: "#000000",
            lineTension: 0.4
          },
          {
            label: "Impressionen",
            borderColor: "#48c6ef",
            data: impressionsData,
            fill: true,
            backgroundColor: "rgba(72, 198, 239, 0.8)",
            pointHoverBackgroundColor: "#000000",
            lineTension: 0.4
          },
          {
            label: "CTR",
            borderColor: "#48c6ef",
            data: ctrData,
            fill: true,
            backgroundColor: "rgba(72, 198, 239, 0.8)",
            pointHoverBackgroundColor: "#000000",
            lineTension: 0.4
          }
        ]
      };

      this.$nextTick().then(() => {
        this.$refs.chart.updateChart();
      });
    },
    mode() {
      this.filter.ids = null;
      this.resetSearch = false;
      this.$nextTick(function() {
        this.resetSearch = true;
      });
    }
  },
  methods: {
    setIds(value) {
      if (value) {
        let result = value.map(x => x.adInsightIDs).flat();
        if (result.length > 0) {
          this.filter.ids = result;
        } else {
          this.filter.ids = null;
        }
      } else {
        this.filter.ids = null;
      }
    }
  }
};
</script>

<style lang="scss">
@import "@/assets/scss/components/bi/main";
</style>
